import React, { useEffect, useMemo, useState } from "react";
import { Box, IconButton, Typography, useMediaQuery } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Link as RouterLink } from "react-router-dom";

import ProductCard from "../../../../GlobalFunction/ProductCard";
import { ProductsAPI } from "../../../../../api/Product";
import { getLangFromCookie } from "../../../../../Utils/LangCookie";
import { getPrimaryColor } from "../../../../GlobalFunction/Theme";

function getSuggestionSectionTitle(lang) {
  const L = String(lang || "ZH").toUpperCase();

  if (L === "EN") return "Product Suggestions";
  if (L === "JP") return "関連商品";

  return "相關推薦";
}

function pickLangText(row, key, lang) {
  const L = String(lang || "ZH").toUpperCase();

  const direct =
    L === "EN"
      ? row?.[`${key}_en`]
      : L === "JP"
        ? row?.[`${key}_jp`]
        : row?.[`${key}_zh`];

  return (
    String(direct || "").trim() ||
    String(row?.[`${key}_zh`] || "").trim() ||
    String(row?.[`${key}_en`] || "").trim() ||
    String(row?.[`${key}_jp`] || "").trim() ||
    String(row?.[key] || "").trim()
  );
}

function getProductCode(product) {
  return (
    product?.parentID ||
    product?.productID ||
    product?.productId ||
    product?.id ||
    ""
  );
}

function normalizeSuggestedProduct(product) {
  const code = getProductCode(product);

  return {
    ...product,
    id: product?.productID || product?.id || code,
    productID: product?.productID || product?.id || code,
    parentID: product?.parentID || code,
    productName:
      product?.name ||
      product?.productName ||
      product?.nameZH ||
      product?.parentID ||
      code,
    images: Array.isArray(product?.images) ? product.images : [],
  };
}

function buildRows(groups, lang) {
  const rows = [];

  (Array.isArray(groups) ? groups : []).forEach((group) => {
    (Array.isArray(group?.subgroups) ? group.subgroups : []).forEach(
      (subgroup) => {
        const products = (
          Array.isArray(subgroup?.products) ? subgroup.products : []
        )
          .map(normalizeSuggestedProduct)
          .filter((item) => getProductCode(item));

        if (!products.length) return;

        rows.push({
          id: `${group?.suggestion_group_id || ""}::${
            subgroup?.suggestion_subgroup_id || ""
          }`,
          title:
            pickLangText(subgroup, "label", lang) ||
            subgroup?.suggestion_subgroup_id ||
            "",
          products,
        });
      },
    );
  });

  return rows;
}

function ProductSuggestionCarouselRow({ row, userID }) {
  const [startIndex, setStartIndex] = useState(0);

  const isTabletVerticalUp = useMediaQuery((theme) =>
    theme.breakpoints.up("sm"),
  );

  const isTabletHorizontalUp = useMediaQuery((theme) =>
    theme.breakpoints.up("md"),
  );

  const products = row.products || [];

  const cardsPerView = isTabletHorizontalUp ? 4 : isTabletVerticalUp ? 3 : 2;

  const visibleCount = Math.min(cardsPerView, products.length);

  const visibleProducts = useMemo(() => {
    if (!products.length) return [];

    return Array.from({ length: visibleCount }, (_, index) => {
      const nextIndex = (startIndex + index) % products.length;
      return products[nextIndex];
    });
  }, [products, startIndex, visibleCount]);

  const canMove = products.length > visibleCount;

  const goPrev = () => {
    if (!products.length) return;
    setStartIndex(
      (current) => (current - 1 + products.length) % products.length,
    );
  };

  const goNext = () => {
    if (!products.length) return;
    setStartIndex((current) => (current + 1) % products.length);
  };

  return (
    <Box sx={{ mb: { xs: 4, md: 5 } }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: 18, md: 22 },
            fontWeight: 700,
            color: "text.primary",
          }}
        >
          {row.title}
        </Typography>

        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton
            onClick={goPrev}
            disabled={!canMove}
            sx={{
              width: 38,
              height: 38,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <ChevronLeftIcon />
          </IconButton>

          <IconButton
            onClick={goNext}
            disabled={!canMove}
            sx={{
              width: 38,
              height: 38,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            sm: "repeat(3, minmax(0, 1fr))",
            md: "repeat(4, minmax(0, 1fr))",
          },
          gap: { xs: 1.5, md: 2 },
        }}
      >
        {visibleProducts.map((item) => {
          const code = getProductCode(item);

          return (
            <ProductCard
              key={`${row.id}-${code}`}
              product={item}
              to={`/商品資訊/${encodeURIComponent(code)}`}
              view="grid"
              compact
              imageHeight={160}
              titleLines={2}
              userID={userID}
            />
          );
        })}
      </Box>
    </Box>
  );
}

export default function ProductSuggestionSection({
  productCode,
  lang,
  userID,
}) {
  const [activeLang, setActiveLang] = useState(
    () => lang || getLangFromCookie() || "ZH",
  );
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setActiveLang(lang || getLangFromCookie() || "ZH");
  }, [lang]);

  useEffect(() => {
    const syncLang = () => setActiveLang(getLangFromCookie() || "ZH");

    window.addEventListener("app:languageChanged", syncLang);
    return () => window.removeEventListener("app:languageChanged", syncLang);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadSuggestions() {
      if (!productCode) {
        setGroups([]);
        return;
      }

      setLoading(true);

      try {
        const res = await ProductsAPI.suggestions({
          productCode,
          lang: activeLang,
        });

        const nextGroups = res?.data?.groups || res?.groups || [];

        if (!cancelled) {
          setGroups(Array.isArray(nextGroups) ? nextGroups : []);
        }
      } catch (error) {
        console.error("Failed to load product suggestions", error);

        if (!cancelled) {
          setGroups([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadSuggestions();

    return () => {
      cancelled = true;
    };
  }, [productCode, activeLang]);

  const rows = useMemo(
    () => buildRows(groups, activeLang),
    [groups, activeLang],
  );

  if (loading || !rows.length) {
    return null;
  }

  return (
    <Box
      sx={{
        mt: { xs: 6, md: 8 },
        pt: { xs: 4, md: 5 },
        // borderTop: "1px solid",
        // borderColor: "#D9D9D9",
      }}
    >
      <Typography
        sx={{
          fontSize: { xs: 26, md: 34 },
          fontWeight: 700,
          color: getPrimaryColor("b2b"),
          mb: { xs: 4, md: 5 },
          lineHeight: 1.2,
          borderBottom: "1px solid",
          borderColor: "#D9D9D9",
        }}
      >
        {getSuggestionSectionTitle(activeLang)}
      </Typography>
      {rows.map((row) => (
        <ProductSuggestionCarouselRow key={row.id} row={row} userID={userID} />
      ))}
    </Box>
  );
}
